import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Download, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import api from '../../api';
import './AuditLogsPanel.css';

const initialFilters = {
  actor_email: '',
  actor_role: '',
  action: '',
  resource_type: '',
  status: '',
  ip_address: '',
  search: '',
  start_date: '',
  end_date: ''
};

const formatDate = (value) => value ? new Date(value).toLocaleString() : '-';

const getQueryParams = (filters, extra = {}) => Object.fromEntries(
  Object.entries({ ...filters, ...extra }).filter(([, value]) => value !== '' && value !== null && value !== undefined)
);

const getErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => {
      if (typeof item === 'string') return item;
      if (item?.msg) return item.loc ? `${item.loc.join('.')} - ${item.msg}` : item.msg;
      return JSON.stringify(item);
    }).join(' | ');
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return fallback;
};

function AuditLogsPanel() {
  const [filters, setFilters] = useState(initialFilters);
  const [logs, setLogs] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [retentionDays, setRetentionDays] = useState(90);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchLogs = async (requestedPage = page) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/admin/audit', {
        params: getQueryParams(filters, { page: requestedPage, page_size: 50 })
      });
      setLogs(response.data.items || []);
      setTotal(response.data.total || 0);
      setRetentionDays(response.data.retention_days || 90);
      setPage(requestedPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Audit logs could not be loaded.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(1);
    // Filters are intentionally applied by the user through Search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const downloadLogs = async () => {
    try {
      const response = await api.get('/admin/audit/export', {
        params: getQueryParams(filters),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'audit-log.txt';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(getErrorMessage(err, 'Audit log export failed.'));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <section className="audit-panel">
      <div className="audit-panel-header">
        <div>
          <div className="audit-eyebrow"><ShieldCheck size={15} /> Super Admin security trail</div>
          <h2>Audit Logs</h2>
          <p>Who accessed, viewed, created, edited, deleted, uploaded, or exported application data.</p>
        </div>
        <div className="audit-header-actions">
          <span className="audit-retention">Retained for {retentionDays} days</span>
          <button type="button" className="admin-btn-secondary" onClick={downloadLogs} disabled={loading}>
            <Download size={16} /> Download TXT
          </button>
          <button type="button" className="admin-btn-secondary" onClick={() => fetchLogs(1)} disabled={loading} aria-label="Refresh audit logs" title="Refresh audit logs">
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      <div className="audit-filter-grid">
        <label>Email<input name="actor_email" value={filters.actor_email} onChange={updateFilter} placeholder="user@example.com" /></label>
        <label>Role<select name="actor_role" value={filters.actor_role} onChange={updateFilter}><option value="">All roles</option><option value="admin">Admin</option><option value="system_admin">System Admin</option><option value="faculty">Faculty</option><option value="university">University</option><option value="pop">Professor</option><option value="student">Student</option></select></label>
        <label>Action<select name="action" value={filters.action} onChange={updateFilter}><option value="">All actions</option><option value="VIEW">View</option><option value="CREATE">Create</option><option value="UPDATE">Update</option><option value="DELETE">Delete</option><option value="EXPORT">Export</option><option value="FAILED">Failed</option></select></label>
        <label>Status<select name="status" value={filters.status} onChange={updateFilter}><option value="">All statuses</option><option value="success">Success</option><option value="failure">Failure</option></select></label>
        <label>IP address<input name="ip_address" value={filters.ip_address} onChange={updateFilter} placeholder="192.168.1.1" /></label>
        <label>Resource<input name="resource_type" value={filters.resource_type} onChange={updateFilter} placeholder="users, attendance" /></label>
        <label>From<input type="date" name="start_date" value={filters.start_date} onChange={updateFilter} /></label>
        <label>To<input type="date" name="end_date" value={filters.end_date} onChange={updateFilter} /></label>
        <label className="audit-search-field">Search description<input name="search" value={filters.search} onChange={updateFilter} placeholder="Search events" /></label>
        <button type="button" className="audit-search-button" onClick={() => fetchLogs(1)} disabled={loading}><Search size={17} /> Search</button>
        <button type="button" className="audit-clear-button" onClick={() => { setFilters(initialFilters); fetchLogs(1); }}>Clear</button>
      </div>

      {error && <div className="audit-error">{error}</div>}

      <div className="audit-table-wrap">
        <table className="audit-table">
          <thead><tr><th>Time</th><th>Actor</th><th>Role</th><th>Action</th><th>Resource</th><th>Device / IP</th><th>Status</th><th /></tr></thead>
          <tbody>
            {!loading && logs.length === 0 && <tr><td colSpan="8" className="audit-empty">No audit events found.</td></tr>}
            {logs.map((log) => {
              const expanded = expandedId === log.id;
              return <React.Fragment key={log.id}>
                <tr className={expanded ? 'audit-row-expanded' : ''}>
                  <td>{formatDate(log.timestamp)}</td>
                  <td>{log.actor?.email || 'Anonymous'}</td>
                  <td><span className="audit-role">{log.actor?.role || '-'}</span></td>
                  <td><span className={`audit-action audit-action-${log.action.toLowerCase()}`}>{log.action}</span></td>
                  <td>{log.resource?.type || '-'}{log.resource?.id ? ` #${log.resource.id}` : ''}</td>
                  <td>{log.device || '-'}<small>{log.ip_address || '-'}</small></td>
                  <td><span className={`audit-status audit-status-${log.status}`}>{log.status}</span></td>
                  <td><button type="button" className="audit-expand" onClick={() => setExpandedId(expanded ? null : log.id)} aria-label={expanded ? 'Hide audit details' : 'Show audit details'}>{expanded ? <ChevronUp size={17} /> : <ChevronDown size={17} />}</button></td>
                </tr>
                {expanded && <tr className="audit-detail-row"><td colSpan="8"><div className="audit-detail-grid"><div><strong>Description</strong><span>{log.description || '-'}</span></div><div><strong>Route</strong><span>{log.route || '-'}</span></div><div><strong>Request ID</strong><span>{log.request_id || '-'}</span></div><div><strong>User agent</strong><span>{log.user_agent || '-'}</span></div><div><strong>Before</strong><pre>{JSON.stringify(log.before, null, 2) || '-'}</pre></div><div><strong>After</strong><pre>{JSON.stringify(log.after, null, 2) || '-'}</pre></div></div></td></tr>}
              </React.Fragment>;
            })}
          </tbody>
        </table>
      </div>

      <div className="audit-pagination"><span>{total} events</span><div><button type="button" onClick={() => fetchLogs(page - 1)} disabled={loading || page <= 1}>Previous</button><span>Page {page} of {totalPages}</span><button type="button" onClick={() => fetchLogs(page + 1)} disabled={loading || page >= totalPages}>Next</button></div></div>
    </section>
  );
}

export default AuditLogsPanel;